import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { LogOut, Sun, Moon } from 'lucide-react';
import { trackNavigation, trackAuth } from '@/lib/rum';
import { AuthenticatedRoute } from '@/components/AuthenticatedRoute';
import { AppSidebar } from '@/components/app-sidebar';
import { useTheme } from '@/contexts/ThemeContext';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';

export function Layout() {
  const { instance } = useMsal();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();

  // Track page views on route change
  useEffect(() => {
    const pageName = location.pathname.replace('/', '') || 'home';
    trackNavigation.pageViewed(pageName);
  }, [location.pathname]);

  const handleLogout = () => {
    trackAuth.logout();
    instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  };

  const account = instance.getActiveAccount();
  const userEmail = account?.username || 'user@example.com';
  const userName = account?.name || undefined;

  const getInitials = () => {
    if (userName) {
      const names = userName.split(' ');
      return names.length >= 2
        ? `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase()
        : names[0][0].toUpperCase();
    }
    return userEmail[0].toUpperCase();
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        {/* Top header bar */}
        <header className="flex h-14 items-center justify-end border-b bg-background px-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="relative size-9 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {getInitials()}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="bottom" sideOffset={8} className="w-56">
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  {userName && (
                    <p className="text-sm font-medium leading-none">{userName}</p>
                  )}
                  <p className="text-xs leading-none text-muted-foreground">
                    {userEmail}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={toggleTheme}>
                {theme === 'dark' ? (
                  <Sun className="mr-2 size-4" />
                ) : (
                  <Moon className="mr-2 size-4" />
                )}
                <span>{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={handleLogout}>
                <LogOut className="mr-2 size-4" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Main content */}
        <AuthenticatedRoute>
          <div className="flex-1 overflow-auto p-6">
            <Outlet />
          </div>
        </AuthenticatedRoute>
      </SidebarInset>
    </SidebarProvider>
  );
}
